import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/lib/api";
import type { MrpSkuHoverHintFields } from "@/lib/mrp";

import {
  buildKittingCartSlots,
  incomingRowsEligibleForKitting,
  incomingRowsHeldForOpenKittingPickRun,
  kittingCartSlotById,
  kittingFallbackCartSlotForRunKey,
  type KittingCartSlot,
} from "./kittingCartModel";
import {
  applyKittingPickRunQuantitiesToLines,
  buildKittingPickLines,
  kittingPickLinesStillToPick,
  type KittingHubSublocationBalance,
  type KittingPickLine,
} from "./kittingPickModel";
import { buildKittingPickCartEntries, buildKittingPickCartEntriesFromRunItems, type KittingPickCartEntry } from "./kittingPickCartModel";
import { filterPickLinesBySlotIds } from "./kittingOrderSelectionModel";
import type { KittingPickRun } from "./kittingPickRunApi";
import {
  fetchKittingPickData,
  kittingPickDataQueryKey,
  KITTING_PICK_DATA_QUERY_STALE_MS,
  type KittingPickDataApiResponse,
} from "./kittingPickDataApi";

type SkuHoverHintPayload = Record<string, MrpSkuHoverHintFields>;

const EMPTY_INCOMING_ROWS: KittingPickDataApiResponse["incoming_rows"] = [];

function displayNameFromHoverHint(
  cpn: string,
  hoverHints: SkuHoverHintPayload | undefined,
): string {
  const hint = hoverHints?.[cpn];
  if (hint == null) return cpn;
  const parts = [hint.description, hint.variant].filter((part) => part != null && String(part).trim() !== "");
  return parts.length > 0 ? parts.join(" — ") : cpn;
}

export function useKittingPickPageData(
  slotIds: readonly string[],
  run: KittingPickRun | null | undefined,
): {
  cartSlotById: Map<string, KittingCartSlot>;
  pickLines: KittingPickLine[];
  scopedPickLines: KittingPickLine[];
  cartEntries: KittingPickCartEntry[];
  loading: boolean;
  error: ApiError | null;
  hoverHints: SkuHoverHintPayload | undefined;
} {
  const normalizedSlotIds = useMemo(
    () => slotIds.map((slotId) => slotId.trim()).filter((slotId) => slotId.length > 0),
    [slotIds],
  );

  const pickDataQuery = useQuery<KittingPickDataApiResponse, ApiError>({
    queryKey: kittingPickDataQueryKey(normalizedSlotIds),
    queryFn: () => fetchKittingPickData(normalizedSlotIds),
    enabled: normalizedSlotIds.length > 0,
    staleTime: KITTING_PICK_DATA_QUERY_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const incomingRows = pickDataQuery.data?.incoming_rows ?? EMPTY_INCOMING_ROWS;

  const incomingForKitting = useMemo(
    () => incomingRowsEligibleForKitting(incomingRows),
    [incomingRows],
  );

  const incomingForOpenRun = useMemo(
    () => (run == null ? incomingForKitting : incomingRowsHeldForOpenKittingPickRun(incomingRows)),
    [incomingForKitting, incomingRows, run],
  );

  const cartSlots = useMemo(() => {
    const built = buildKittingCartSlots(incomingForOpenRun, {
      retainZeroDemandSlots: run != null,
    });
    if (run == null) return built;
    const byId = kittingCartSlotById(built);
    const missing = run.slotKeys
      .map((slotKey) => (byId.has(slotKey) ? null : kittingFallbackCartSlotForRunKey(slotKey)))
      .filter((slot): slot is KittingCartSlot => slot != null);
    return missing.length === 0 ? built : [...built, ...missing];
  }, [incomingForOpenRun, run]);

  const cartSlotById = useMemo(() => kittingCartSlotById(cartSlots), [cartSlots]);

  const sgsInventoryRows = useMemo(() => {
    const rows = pickDataQuery.data?.inventory_rows ?? [];
    return rows.filter((row) => row.location_code === "sgs");
  }, [pickDataQuery.data?.inventory_rows]);

  const hubSublocationsByCpn = useMemo(() => {
    const map = new Map<string, KittingHubSublocationBalance[]>();
    for (const row of pickDataQuery.data?.sublocation_rows ?? []) {
      if (row.location_code !== "sgs") continue;
      const cpn = String(row.cpn ?? "").trim();
      if (cpn.length === 0) continue;
      const list = map.get(cpn) ?? [];
      list.push({
        sublocation: row.sublocation ?? "",
        quantity: Math.round(Number(row.quantity) || 0),
      });
      map.set(cpn, list);
    }
    return map;
  }, [pickDataQuery.data?.sublocation_rows]);

  const hoverHints = pickDataQuery.data?.sku_hover_hints;

  const pickLines = useMemo(() => {
    if (incomingForKitting.length === 0) return [];
    const built = buildKittingPickLines({
      incomingRows: incomingForKitting,
      inventoryRows: sgsInventoryRows,
      hubSublocationsByCpn,
      cartSlotById,
      displayNameForCpn: (cpn) => displayNameFromHoverHint(cpn, hoverHints),
    });
    if (run == null) return built;
    return applyKittingPickRunQuantitiesToLines(built, [
      ...(run.priorUnabsorbedItems ?? []),
      ...run.items,
    ]);
  }, [incomingForKitting, sgsInventoryRows, hubSublocationsByCpn, cartSlotById, hoverHints, run]);

  const scopedPickLines = useMemo(
    () => kittingPickLinesStillToPick(filterPickLinesBySlotIds(pickLines, normalizedSlotIds)),
    [pickLines, normalizedSlotIds],
  );

  const cartEntries = useMemo(() => {
    if (run != null) {
      return buildKittingPickCartEntriesFromRunItems({
        runItems: run.items,
        cartSlotById,
        pickLines,
        slotIds: normalizedSlotIds,
      });
    }
    return buildKittingPickCartEntries({
      incomingRows: pickDataQuery.data?.incoming_rows ?? [],
      cartSlotById,
      slotIds: normalizedSlotIds,
    });
  }, [run, pickDataQuery.data?.incoming_rows, cartSlotById, pickLines, normalizedSlotIds]);

  return {
    cartSlotById,
    pickLines,
    scopedPickLines,
    cartEntries,
    loading: pickDataQuery.isLoading,
    error: pickDataQuery.error ?? null,
    hoverHints,
  };
}
